import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Styles from './Styles.module.css';
import TextArea from '../../../components/Modal/TextArea';
import SelectBox from '../../../components/SelectBoxButton';
import Button from '../../../components/Button';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const NotesSection = ({ showNotes, setShowNotes, roomId, currentUserId }) => {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState({ value: '', error: '' });
  const [noteType, setNoteType] = useState({ value: 'internal', error: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [previousNotes, setPreviousNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const fetchPreviousNotes = useCallback(async () => {
    if (!roomId) return;
    setLoadingNotes(true);
    try {
      const resp = await axios.get(`${BASE_URL}/api/v1/video-notes`, {
        params: { roomId },
        withCredentials: true,
      });
      setPreviousNotes(resp.data?.data?.data || []);
    } catch (_) {
      setPreviousNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (showPrevious) fetchPreviousNotes();
  }, [showPrevious, fetchPreviousNotes]);

  const handleSave = async () => {
    if (!noteText.value.trim()) {
      setNoteText(prev => ({ ...prev, error: t('screens.video.group.notesEnterError') }));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BASE_URL}/api/v1/video-notes`, {
        noteText: noteText.value.trim(),
        noteType: noteType.value,
        roomId: roomId || undefined,
      }, { withCredentials: true });
      setNoteText({ value: '', error: '' });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (showPrevious) fetchPreviousNotes();
    } catch (err) {
      setNoteText(prev => ({ ...prev, error: t('screens.video.group.notesSaveError') }));
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
  };

  return (
    <div className={`${Styles.notesSection} ${showNotes ? '' : Styles.hide}`}>
      <div className='video_chat_chats'>
        <div className='video_chat_chats_scroll' style={{ height: 'inherit', overflowY: 'auto', padding: '0 4px' }}>
          <br />
          {!showPrevious ? (
            <>
              <TextArea
                placeholder={t('screens.video.call.writeNoteHere')}
                rows={5}
                value={noteText.value}
                onChange={e => setNoteText({ value: e.target.value, error: '' })}
                errorMessage={noteText.error}
              />

              <SelectBox
                buttons={[
                  {
                    label: t('screens.video.call.internalNote'),
                    value: 'internal',
                    onClick: () => setNoteType({ value: 'internal', error: '' }),
                  },
                  {
                    label: t('screens.video.call.sharedWithUser'),
                    value: 'public',
                    onClick: () => setNoteType({ value: 'public', error: '' }),
                  },
                ]}
                value={noteType.value}
              />

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <Button
                  name={saving ? t('screens.video.group.notesSaving') : saved ? t('screens.video.group.notesSaved') : t('screens.video.group.notesSave')}
                  onClick={handleSave}
                  disabled={saving}
                />
                <Button
                  name={t('screens.video.group.notesPrevious')}
                  onClick={() => setShowPrevious(true)}
                />
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{t('screens.video.group.notesPreviousTitle')}</span>
                <button
                  onClick={() => setShowPrevious(false)}
                  style={{ background: 'none', border: 'none', fontSize: 12, color: '#64748b', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {t('screens.video.group.notesBack')}
                </button>
              </div>

              {loadingNotes ? (
                <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center', padding: 20 }}>{t('screens.video.group.notesLoading')}</div>
              ) : previousNotes.length === 0 ? (
                <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center', padding: 20 }}>{t('screens.video.group.notesEmpty')}</div>
              ) : (
                previousNotes.map((note, i) => (
                  <div key={note._id || i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 600, color: note.noteType === 'internal' ? '#7c3aed' : '#059669' }}>
                        {note.noteType === 'internal' ? t('screens.video.group.noteInternal') : t('screens.video.group.noteShared')}
                      </span>
                      <span>{formatTime(note.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{note.noteText}</div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotesSection;
